import { useApi } from '@/api';
import { Asset, Page, PageSkeleton } from '@/components';
import { useParams } from 'react-router';
import { useTableProps } from '@blockanalitica/ui';
import { useMemo, useCallback } from 'react';
import MarketWalletsBorrowersTable from './MarketWalletsBorrowersTable';
import MarketWalletsSuppliersTable from './MarketWalletsSuppliersTable';
import _ from 'lodash';
import {
  Flex,
  TextSearchFilter,
  DisplaySwitch,
  EmptyBox,
  Card
} from '@blockanalitica/ui';
import { useSearchParams } from '@/hooks';

type MarketResponse = {
  underlying_symbol: string;
  underlying_name: string;
  status: {
    borrow: string;
    collateral: string;
    supply: string;
    frozen: string;
  };
};

export default function MarketWalletsPage() {
  const { marketAddress, network } = useParams() as {
    marketAddress: string;
    network: string;
  };
  const [wallets, setWalletsParam] = useSearchParams('type', 'suppliers');

  const displayOptions = useMemo(
    () => [
      { key: 'suppliers', value: 'Suppliers' },
      { key: 'borrowers', value: 'Borrowers' }
    ],
    []
  );
  const handleDisplayChange = useCallback(
    (newValue: string) => {
      setWalletsParam(newValue);
    },
    [setWalletsParam]
  );

  const { pagination, filters } = useTableProps({
    pageSize: 10,
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: ''
    }
  });

  const [data, error] = useApi<MarketResponse>({
    url: `/markets/${marketAddress}/`
  });

  if (error) {
    throw error;
  }

  if (data) {
    let content;
    if (_.isEmpty(data)) {
      content = <EmptyBox />;
    } else if (wallets === 'suppliers') {
      content = (
        <MarketWalletsSuppliersTable
          marketAddress={marketAddress}
          network={network}
          pagination={pagination}
          textSearch={filters.filtersApplied.textSearch}
        />
      );
    } else {
      content = (
        <MarketWalletsBorrowersTable
          marketAddress={marketAddress}
          network={network}
          pagination={pagination}
          textSearch={filters.filtersApplied.textSearch}
          tag={data.status['borrow']}
          symbol={data.underlying_symbol}
        />
      );
    }

    return (
      <Page
        title={
          <Asset
            symbol={data.underlying_symbol}
            address={marketAddress}
            network={network}
            iconSize="4"
            fontSize="5"
            suffix="Wallets"
          />
        }
        actions={
          <Flex flexDirection={['column', 'row']} gap={['s', 'xs']}>
            <DisplaySwitch
              data={displayOptions}
              activeOption={wallets}
              onChange={handleDisplayChange}
            />
            <TextSearchFilter
              placeholder="Search by wallet"
              pagination={pagination}
              flex="1"
              display="flex"
              options={{
                variants: {
                  textSearchFilter: 'fullWidth'
                }
              }}
              {...filters}
            />
          </Flex>
        }>
        <Card gap="xs">{content}</Card>
      </Page>
    );
  }

  return <PageSkeleton />;
}
